import EditIcon from "@mui/icons-material/Edit";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { AppBar, IconButton, Toolbar } from "@mui/material";
import Alert from "@mui/material/Alert";
import Backdrop from "@mui/material/Backdrop";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { blueGrey } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { FC } from "react";
import AccountDropdown from "../../components/AccountDropdown/AccountDropdown";
import Copyright from "../../components/Copyright";
import AdditionalCosts from "../../components/orderSummary/AdditionalCosts/AdditionalCosts";
import CustomSalesTax from "../../components/orderSummary/CustomSalesTax/CustomSalesTax";
import DoorList from "../../components/orderSummary/DoorList/DoorList";
import MarginInput from "../../components/orderSummary/MarginInput/MarginInput";
import AdminCostModal from "../../components/orderSummary/modals/AdminCost/AdminCostModal";
import FreightWarningModal from "../../components/orderSummary/modals/FreightWarning/FreightWarningModal";
import PoJobsite from "../../components/orderSummary/PoJobsite";
import SalesTaxTooltip from "../../components/orderSummary/SalesTaxTooltip";
import Settings from "../../components/orderSummary/Settings/Settings";
import OtsmIcon from "../../components/OtsmIcon";
import QuoteNumber from "../../components/QuoteNumber";
import CustomerPDF from "../../quotePDF/CustomerPdfButton";
import DealerPDF from "../../quotePDF/DealerPdfButton";
import {
  calcDealerTaxDollar,
  calcSalesTaxDollar,
} from "../../util/calcSalesTaxDollar";
import dealerRoles from "../../util/dealerAccounts";
import formatDoubleWithCommas from "../../util/formatDoubleWithCommas";
import formatIntWithCommas from "../../util/formatIntWithCommas";
import priceWithZeros from "../../util/priceWithZeros";
import { getDealerTax } from "./orderSummary.biz";
import useSummarySelector from "./useSummarySelector";
import { CustomCladColorInput } from "../../components/CustomCladColorInput/CustomCladColorInput";
import { FreightQuotePriceInput } from "../../components/FreightQuotePriceInput/FreightQuotePriceInput";
import RecalculatingWarningModal from "../../components/orderSummary/modals/FreightWarning/FreightWarningModal";

export function allDealerActAccess(role: string) {
  return dealerRoles.includes(role);
}

const StyledDivBg = styled("div")({
  backgroundColor: blueGrey[50],
  backgroundSize: "cover",
  minHeight: "100vh",
  position: "relative",
});

const StyledDivContentWrap = styled("div")({
  paddingBottom: "2.5rem",
});

const StyledAppbar = styled(AppBar)({
  marginBottom: "5vh",
});

const StyledToolbar = styled(Toolbar)({
  display: "flex",
  justifyContent: "space-between",
});

const StyledNavBtn = styled(Button)({
  color: "white",
});

const StyledDivCenter = styled("div")({
  display: "flex",
  justifyContent: "center",
});

const StyledImgLarg = styled("img")({
  height: 80,
  width: 300,
});

const StyledLogoSmall = styled("img")({
  height: 60,
  width: 45,
  marginLeft: 10,
});

const StyledSpanPO = styled("span")({
  color: "red",
  fontSize: 22,
});

const StyledQuoteSummaryItemList = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "5px",
});

const StyledDivColCenter = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  border: "2px solid grey",
  backgroundColor: "white",
});

const StyledTypographyTitle = styled(Typography)({
  fontWeight: "bold",
  borderBottom: "1px solid black",
  alignSelf: "center",
  marginBottom: 10,
});

const StyledDivRowCenter = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const StyledDivRowBtwn = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  paddingLeft: 10,
  paddingRight: 10,
});

const StyledDivRowSpace = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  paddingLeft: 10,
  paddingRight: 10,
});

const StyledDivRowSpacePad = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  borderBottom: "1px solid black",
  paddingLeft: 10,
  paddingRight: 10,
  paddingBottom: 10,
});

const StyledDivColStart = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
});

const StyledDivColEnd = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
  flexDirection: "column",
  alignItems: "flex-end",
});

const StyledDivAddCosts = styled("div")({
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  borderBottom: "1px solid black",
  paddingBottom: 20,
});

const StyledCalcButton = styled(Button)({
  textAlign: "center",
  color: "#21c7f5",
  borderRadius: 5,
  fontWeight: "bold",
  backgroundColor: "#eceff1",
  marginLeft: 20,
  marginRight: 20,
  marginTop: 5,
});

const StyledDivRowSpaceBtw = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  paddingLeft: 10,
  paddingRight: 10,
  paddingTop: 20,
  position: "relative",
});

const StyledGrandDiv = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  marginTop: 10,
  marginBottom: 30,
  paddingLeft: 10,
  paddingRight: 10,
  borderBottom: "1px solid black",
  paddingBottom: 10,
});

const StyledDivCustomPaint = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  marginTop: 10,
  textAlign: "center",
  backgroundColor: "white",
});

const StyledBackdrop = styled(Backdrop)({
  backgroundColor: "#5b6467",
  backgroundImage: "linear-gradient(315deg, #5b6467 0%, #8b939a 74%)",
});

const StyledLoadDiv = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
});

const StyledNavbarLeftDiv = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "50%",
  [theme.breakpoints.down("sm")]: {
    justifyContent: "flex-start",
    width: "40%",
  },
}));

const StyledNavbarRightDiv = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-evenly",
  alignItems: "center",
  width: "30%",
  [theme.breakpoints.down("sm")]: {
    justifyContent: "space-between",
    width: "60%",
  },
}));

const StyledTitleDiv = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  paddingTop: -20,
  [theme.breakpoints.down("lg")]: {
    paddingTop: 100,
    justifyContent: "center",
  },
}));

const StyledTitleAndUnitsDIv = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  paddingLeft: 100,
  [theme.breakpoints.down("sm")]: {
    padding: 0,
    alignItems: "center",
  },
}));

const StyledOSContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  paddingLeft: 30,
  paddingRight: 30,
  marginTop: 10,
  [theme.breakpoints.down("lg")]: {
    flexDirection: "column",
    padding: 20,
  },
  [theme.breakpoints.down("sm")]: {
    padding: 0,
  },
}));

const StyledPricingDiv = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  flexBasis: "25%",
  marginTop: -100,
  backgroundColor: blueGrey[50],
  [theme.breakpoints.down("lg")]: {
    padding: 40,
    margin: 0,
  },
  [theme.breakpoints.down("sm")]: {
    height: "80%",
    flexBasis: "100%",
    padding: 10,
    justifyContent: "flex-start",
  },
}));

const StyledPoContainerDiv = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 10,
  border: "2px solid grey",
  padding: 5,
  paddingTop: 10,
  paddingBottom: 10,
  backgroundColor: "white",
  [theme.breakpoints.down("sm")]: {
    padding: 5,
  },
}));

const StyledTitleText = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    fontSize: 30,
    fontWeight: "400",
  },
}));

const StyledButtonsLayout = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  justifyContent: "space-around",
  position: "relative",
  width: "100%",
  [theme.breakpoints.down("lg")]: {
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const StyledBackdrop2 = styled(Backdrop)({
  zIndex: 20,
  color: "#fff",
});

const StyledDivCol = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
});

const StyledText = styled(Typography)({
  color: "white",
  paddingTop: 10,
});

const StyledEditIcon = styled(IconButton)({
  width: 17,
  height: 17,
});

const StyledEditButton = styled(Button)(({ theme }) => ({
  width: 75,
  height: 24,
  color: "white",
}));

const Loader: FC = () => {
  return (
    <div style={{ width: 18, height: 18, marginLeft: 10 }}>
      <CircularProgress size={18} color="primary" />
    </div>
  );
};

const OrderSummary: FC = () => {
  const {
    dealerCostWithAdminCharge,
    handleClose,
    handleOpen,
    openModal,
    handleRL,
    handleReturnDealerOrder,
    handleReturn,
    calcGrandTotal,
    handleGenerateClick,
    handlePoChange,
    handleModalDetails,
    handleJobsiteChange,
    modalDetailsErr,
    handleClosePO,
    handleOpenPO,
    handleCloseJobsite,
    handleOpenJobsite,
    openPO,
    loadJobsite,
    openJobsite,
    handleTaxChange,
    taxResults,
    withMarkup,
    setWithMarkup,
    margin,
    pdfErr,
    priceWithMargin,
    errMessageMargin,
    setErrMessage,
    custom,
    disableMargin,
    disableMarkup,
    disableChanges,
    adminChargeMargined,
    hideCustomerPricing,
    setHideCustomerPricing,
    overrideFixedMargin,
    setOverrideMarkup,
    overrideFixedMarkup,
    series,
    setSeries,
    addCosts,
    dealerTax,
    isLoading,
    error,
    data,
    isFetching,
    userData,
    isLoadingUser,
    po,
    marginInput,
    setMarginInput,
    grandTotal,
    changeLogo,
    matches,
    historyState,
    jobsite,
    setOverride,
    chosenTax,
    load,
    dealerUrl,
    customerUrl,
    openCustomerEmailModal,
    handleOpenCustomerEmailModal,
    handleCloseCustomerEmailModal,
    openWarning,
    openRecalculatingWarning,
    handleOpenRecalculatingWarning,
    handleCloseRecalculatingWarning,
    handleOpenWarning,
    handleCloseWarning,
    base64Pdf,
    resetPDF,
    freightData,
    fetchingFreight,
    handleAddress,
    handleEditPoClick,
    editPoClicked,
    handleUpdatePoClick,
    isUpdatingPo,
    canEditCustomCladColorAndFreightQuotePrice,
  } = useSummarySelector();

  if (error) return <div>An Error Occured. Try Refreshing the Page.</div>;

  if (isLoading || isLoadingUser) {
    return (
      <StyledBackdrop open={true}>
        <StyledLoadDiv>
          <img
            src="https://res.cloudinary.com/ag-millworks/image/upload/Calculating_doorlogo.png"
            alt="door logo"
          />
          <Typography
            variant="h5"
            style={{
              color: "white",
              paddingTop: 10,
            }}
          >
            Loading Quote Summary...
          </Typography>
        </StyledLoadDiv>
      </StyledBackdrop>
    );
  }

  return (
    <StyledDivBg>
      <StyledDivContentWrap>
        {(userData.signInRole === "Inside Sales" ||
          userData.signInRole === "Admin") && (
          <AdminCostModal
            open={openModal}
            handleClose={handleClose}
            charge={data?.quote.adminCharge || ""}
            chargeLabel={data?.quote.adminChargeLabel || ""}
            withMargin={
              typeof data?.quote.adminChargeMargined === "boolean"
                ? data?.quote.adminChargeMargined
                : ""
            }
            quoteId={data?.quote._id}
            userId={historyState.userId}
            isFetching={isFetching}
            dealerTax={dealerTax || 0}
          />
        )}

        <RecalculatingWarningModal
          open={openRecalculatingWarning}
          handleClose={handleCloseRecalculatingWarning}
        />

        <div style={{ position: "relative" }}>
          <StyledAppbar
            color="customGrey"
            position={matches ? "static" : "fixed"}
          >
            <StyledToolbar>
              <StyledNavbarLeftDiv>
                <StyledNavBtn
                  variant="contained"
                  color="cyan"
                  onClick={
                    disableChanges &&
                    (allDealerActAccess(userData.signInRole) ||
                      userData.signInRole === "Outside Sales")
                      ? handleReturnDealerOrder
                      : handleReturn
                  }
                  size="small"
                >
                  <NavigateBeforeIcon style={{ display: "inline" }} />{" "}
                  {matches &&
                  disableChanges &&
                  (allDealerActAccess(userData.signInRole) ||
                    userData.signInRole === "Outside Sales")
                    ? `Return to ${
                        data?.quote?.status === "Archived" ? "Quotes" : "Orders"
                      }`
                    : matches
                      ? "Return to Cart"
                      : ""}
                </StyledNavBtn>
                <StyledDivCenter>
                  {changeLogo ? (
                    <StyledImgLarg
                      src="https://res.cloudinary.com/ag-millworks/image/upload/AG_Logos/AGlogoSmall.png"
                      alt="Company Logo"
                    />
                  ) : (
                    <StyledLogoSmall
                      src="https://res.cloudinary.com/ag-millworks/image/upload/v1640825559/doorlogo.png"
                      alt=""
                    />
                  )}
                </StyledDivCenter>
              </StyledNavbarLeftDiv>

              <StyledNavbarRightDiv>
                {userData.otsm && allDealerActAccess(userData.signInRole) && (
                  <OtsmIcon />
                )}

                <QuoteNumber quoteNumber={data?.quote.quoteNumber} />

                <Button
                  onClick={handleRL}
                  variant="contained"
                  size="small"
                  color="cyan"
                  style={{
                    color: "white",
                    fontSize: !matches ? 8 : "",
                  }}
                >
                  Resume Later
                </Button>

                <AccountDropdown
                  color="white"
                  resumeLaterBtn={true}
                  returnedPath="orderSummary"
                  historyState={historyState}
                />
              </StyledNavbarRightDiv>
            </StyledToolbar>
          </StyledAppbar>
        </div>

        <StyledTitleDiv>
          <StyledTitleAndUnitsDIv>
            <StyledTitleText variant="h3">
              {data?.quote?.status === "Order" ? "ORDER" : "QUOTE"} SUMMARY
            </StyledTitleText>
            <Typography variant="body1">
              {data?.quote.totalUnits}{" "}
              {data?.quote.totalUnits === 1
                ? "Item in Quote"
                : "Items In Quote"}
            </Typography>
          </StyledTitleAndUnitsDIv>
        </StyledTitleDiv>

        <StyledOSContainer>
          <DoorList
            otsm={userData.otsm}
            signInRole={userData.signInRole}
            quote={data?.quote}
          />

          <StyledPricingDiv>
            {!data?.quote.checkedJobsite ? (
              <StyledPoContainerDiv>
                <label style={{ fontWeight: "bold" }}>
                  PO #: <StyledSpanPO> *</StyledSpanPO>
                </label>
                <input
                  maxLength={20}
                  style={{ width: 150, marginLeft: 10 }}
                  value={po}
                  onChange={(e) => handlePoChange(e)}
                  name="po"
                  type="text"
                  placeholder="PO #"
                  disabled={
                    data?.quote.status !== "Quote" &&
                    (!editPoClicked || isUpdatingPo)
                  }
                />
                {data?.quote.status !== "Quote" &&
                  (!editPoClicked ? (
                    <StyledEditIcon
                      size="small"
                      style={{ marginLeft: 10 }}
                      onClick={handleEditPoClick}
                    >
                      <EditIcon color="primary" />
                    </StyledEditIcon>
                  ) : !isUpdatingPo ? (
                    <StyledEditButton
                      variant="contained"
                      color="primary"
                      size="small"
                      style={{ marginLeft: 10 }}
                      onClick={() => handleUpdatePoClick(po)}
                      // disabled={btnDisabled}
                    >
                      Confirm
                    </StyledEditButton>
                  ) : (
                    <Loader />
                  ))}
              </StyledPoContainerDiv>
            ) : (
              <PoJobsite
                po={po}
                jobsite={jobsite}
                handlePoChangeModal={handlePoChange}
                disableChanges={disableChanges}
                handleJobsiteChange={handleJobsiteChange}
                handleAddress={handleAddress}
                handleModalDetails={handleModalDetails}
                loadJobsite={loadJobsite}
                openJobsite={openJobsite}
                openPO={openPO}
                handleCloseJobsite={handleCloseJobsite}
                handleOpenJobsite={handleOpenJobsite}
                handleClosePO={handleClosePO}
                handleOpenPO={handleOpenPO}
                err={modalDetailsErr}
                taxResults={taxResults}
                handleTaxChange={handleTaxChange}
                chosenTax={chosenTax}
                handleEditPoClick={handleEditPoClick}
                editPoClicked={editPoClicked}
                handleUpdatePoClick={handleUpdatePoClick}
                isUpdatingPo={isUpdatingPo}
                status={data?.quote.status}
              />
            )}

            <StyledDivColCenter>
              <StyledDivRowCenter>
                <div />

                <StyledTypographyTitle variant="body1">
                  {disableChanges ? "ORDER" : "QUOTE"} SUMMARY
                </StyledTypographyTitle>

                {userData.signInRole !== "Outside Sales" &&
                  userData.signInRole !== "Inside Sales" && (
                    <Settings
                      resetPDF={resetPDF}
                      markupChecked={withMarkup}
                      fixedMargin={userData.fixedMargin || false}
                      fixedMarkup={userData.fixedMarkup || false}
                      setMarginInput={setMarginInput}
                      marginInput={marginInput}
                      otsm={userData.otsm}
                      setOverride={
                        !withMarkup ? setOverride : setOverrideMarkup
                      }
                      override={
                        !withMarkup ? overrideFixedMargin : overrideFixedMarkup
                      }
                      setHideCustomer={setHideCustomerPricing}
                      hideCustomer={hideCustomerPricing}
                      quoteId={data?.quote._id}
                      series={series}
                      setSeries={setSeries}
                    />
                  )}
              </StyledDivRowCenter>

              {(!userData.otsm || !allDealerActAccess(userData.signInRole)) && (
                <StyledQuoteSummaryItemList>
                  <StyledDivRowBtwn>
                    <Typography variant="body2">Factor:</Typography>
                    <Typography variant="body2">
                      {data?.quote.factor}
                    </Typography>
                  </StyledDivRowBtwn>

                  <StyledDivRowBtwn>
                    <Typography variant="body2">Total:</Typography>
                    <Typography variant="body2">
                      ${data?.quote.suggestedRetailPrice}
                    </Typography>
                  </StyledDivRowBtwn>
                  {data?.quote.customCladColorOverMaxApplies ? (
                    <StyledDivRowSpace>
                      <Typography variant="body2" style={{ width: "300px" }}>
                        Custom Clad Color Charge:
                      </Typography>
                      {canEditCustomCladColorAndFreightQuotePrice ? (
                        <CustomCladColorInput
                          quoteId={data?.quote._id}
                          customCladColorPriceUpdateRequired={
                            data?.quote.customCladColorPriceUpdateRequired ||
                            false
                          }
                          initialCustomCladColorPrice={
                            data?.quote?.customCladPrice
                          }
                        />
                      ) : data?.quote.customCladColorPriceUpdateRequired ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                          }}
                        >
                          <Typography
                            variant="body2"
                            style={{ color: "red", textAlign: "end" }}
                          >
                            Contact office for new custom clad color estimate
                          </Typography>
                        </div>
                      ) : (
                        <Typography variant="body2">
                          ${formatDoubleWithCommas(data?.quote.customCladPrice)}
                        </Typography>
                      )}
                    </StyledDivRowSpace>
                  ) : data?.quote.customCladPrice ? (
                    <StyledDivRowBtwn>
                      <Typography variant="body2">
                        Custom Clad Color Charge:
                      </Typography>
                      <Typography variant="body2">
                        ${formatDoubleWithCommas(data?.quote.customCladPrice)}
                      </Typography>
                    </StyledDivRowBtwn>
                  ) : null}

                  {data?.quote.splitFinishPrice &&
                  data?.quote.splitFinishPrice !== 0 ? (
                    <StyledDivRowBtwn>
                      <Typography variant="body2">
                        Split Finish Charge:
                      </Typography>
                      <Typography variant="body2">
                        ${formatIntWithCommas(data?.quote.splitFinishPrice)}
                        .00
                      </Typography>
                    </StyledDivRowBtwn>
                  ) : null}

                  {data?.quote.adminCharge !== 0 &&
                    data?.quote.adminChargeLabel && (
                      <StyledDivRowBtwn>
                        <Typography variant="body2">
                          {data?.quote.adminChargeLabel}:
                        </Typography>
                        <Typography variant="body2">
                          {data?.quote.adminCharge.toString().includes("-") &&
                            "-"}{" "}
                          $
                          {formatIntWithCommas(
                            priceWithZeros(Math.abs(data?.quote.adminCharge)),
                          )}
                        </Typography>
                      </StyledDivRowBtwn>
                    )}

                  {userData && dealerTax && (
                    <StyledDivRowSpace
                      style={{ position: "relative", paddingBottom: 20 }}
                    >
                      <Typography variant="body2">Sales Tax:</Typography>
                      <Typography variant="body2">
                        {userData.dealerTax}%{" "}
                      </Typography>
                      <div
                        style={{
                          position: "absolute",
                          right: 10,
                          top: 10,
                        }}
                      >
                        <Typography style={{ fontSize: 10 }} variant="caption">
                          (
                          {calcDealerTaxDollar({
                            salesTax: userData.dealerTax,
                            retailCost: data?.quote.suggestedRetailPrice,
                            cladCost: data?.quote.customCladPrice,
                            adminCharge: data?.quote.adminCharge || 0,
                            splitFinish: data?.quote.splitFinishPrice || 0,
                          })}
                          )
                        </Typography>
                      </div>
                    </StyledDivRowSpace>
                  )}

                  {data?.quote.state === "CA" ? (
                    <StyledDivRowSpace>
                      <Typography variant="body2">Delivery Charge:</Typography>
                      <Typography variant="body2">
                        $
                        {data?.quote.deliveryCharge
                          ? `${data?.quote.deliveryCharge}.00`
                          : 0}
                      </Typography>
                    </StyledDivRowSpace>
                  ) : freightData.freightQuoteError ? (
                    <>
                      <StyledDivRowSpace>
                        <Typography variant="body2">Freight:</Typography>
                        {canEditCustomCladColorAndFreightQuotePrice ? (
                          <FreightQuotePriceInput
                            quoteId={data?.quote._id}
                            initialFreightQuotePrice={
                              freightData.rateQuoteCharge
                            }
                            initialFreightQuotePriceUpdateRequired={
                              freightData.freightQuotePriceUpdateRequired ??
                              false
                            }
                          />
                        ) : freightData.freightQuotePriceUpdateRequired ? (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                            }}
                          >
                            <Typography
                              variant="body2"
                              style={{ color: "red", textAlign: "end" }}
                            >
                              Contact office for new freight estimate
                            </Typography>
                          </div>
                        ) : (
                          <Typography variant="body2">
                            $
                            {formatDoubleWithCommas(
                              freightData.rateQuoteCharge ?? 0,
                            )}
                          </Typography>
                        )}
                      </StyledDivRowSpace>
                      <StyledDivRowSpace>
                        <Typography variant="body2">Crate:</Typography>
                        <Typography variant="body2">
                          $
                          {formatIntWithCommas(
                            priceWithZeros(freightData.crateCharge ?? ""),
                          )}
                        </Typography>
                      </StyledDivRowSpace>
                    </>
                  ) : !fetchingFreight &&
                    freightData?.crateCharge &&
                    freightData?.rateQuoteCharge ? (
                    <>
                      <StyledDivRowSpace>
                        <Typography variant="body2">Freight:</Typography>
                        <Typography variant="body2">
                          $
                          {formatIntWithCommas(
                            priceWithZeros(freightData.rateQuoteCharge),
                          )}
                        </Typography>
                      </StyledDivRowSpace>
                      <StyledDivRowSpace>
                        <Typography variant="body2">Crate:</Typography>
                        <Typography variant="body2">
                          $
                          {formatIntWithCommas(
                            priceWithZeros(freightData.crateCharge),
                          )}
                        </Typography>
                      </StyledDivRowSpace>
                    </>
                  ) : fetchingFreight ? (
                    <>
                      <StyledDivRowSpace>
                        <Typography variant="body2">Freight:</Typography>
                        <Typography variant="body2">Calculating...</Typography>
                      </StyledDivRowSpace>
                      <StyledDivRowSpace>
                        <Typography variant="body2">Crate:</Typography>
                        <Typography variant="body2">Calculating...</Typography>
                      </StyledDivRowSpace>
                    </>
                  ) : null}

                  <StyledDivRowSpacePad>
                    <StyledDivColStart
                      style={{
                        justifyContent:
                          userData.signInRole === "Inside Sales" ||
                          userData.signInRole === "Admin"
                            ? "flex-end"
                            : "flex-start",
                      }}
                    >
                      <Typography
                        style={{ fontWeight: "bold" }}
                        variant="body2"
                      >
                        Dealer Net Cost:
                      </Typography>

                      {(userData.signInRole === "Inside Sales" ||
                        userData.signInRole === "Admin") && (
                        <button
                          onClick={handleOpen}
                          style={{
                            backgroundColor: "white",
                            color: "black",
                            borderRadius: 4,
                          }}
                        >
                          Add a Charge
                        </button>
                      )}
                    </StyledDivColStart>

                    <StyledDivColEnd>
                      <Typography
                        style={{ fontWeight: "bold" }}
                        variant="body2"
                      >
                        $
                        {!data?.quote.adminCharge || dealerTax
                          ? data?.quote.grandTotal
                          : priceWithZeros(dealerCostWithAdminCharge || "")}
                      </Typography>
                      {data?.quote?.status === "Quote" ||
                      data?.quote?.status === "Archived" ? (
                        <Typography
                          style={{ fontWeight: "bold" }}
                          variant="caption"
                        >
                          Price is valid for 14 days
                        </Typography>
                      ) : null}
                    </StyledDivColEnd>
                  </StyledDivRowSpacePad>
                </StyledQuoteSummaryItemList>
              )}

              {(allDealerActAccess(userData.signInRole) ||
                userData.signInRole === "Admin") && (
                <>
                  {(!userData.otsm ||
                    !allDealerActAccess(userData.signInRole)) && (
                    <MarginInput
                      resetPDF={resetPDF}
                      marginInput={marginInput}
                      setMarginInput={setMarginInput}
                      errMessageMargin={errMessageMargin}
                      setErrMessage={setErrMessage}
                      disableMargin={
                        !withMarkup ? disableMargin : disableMarkup
                      }
                      withMarkup={withMarkup}
                      setWithMarkup={setWithMarkup}
                      fixedMargin={userData.fixedMargin}
                      fixedMarkup={userData.fixedMarkup}
                      quoteStatus={data?.quote.status}
                    />
                  )}

                  <StyledDivAddCosts>
                    <AdditionalCosts
                      disableChanges={disableChanges}
                      costsArray={addCosts}
                      quoteId={data?.quote._id}
                      margin={marginInput}
                      withMarkup={
                        userData.otsm &&
                        userData.otsmDefault === "Fixed Markup" &&
                        userData.fixedMarkup
                          ? true
                          : userData.otsm &&
                              userData.otsmDefault === "Fixed Margin" &&
                              userData.fixedMargin
                            ? false
                            : withMarkup
                      }
                      hasDealerTax={dealerTax ? true : false}
                      isFetching={isFetching}
                    />

                    <StyledCalcButton
                      onClick={() =>
                        calcGrandTotal(
                          marginInput,
                          userData.otsm &&
                            userData.otsmDefault === "Fixed Markup" &&
                            userData.fixedMarkup
                            ? true
                            : userData.otsm &&
                                userData.otsmDefault === "Fixed Margin" &&
                                userData.fixedMargin
                              ? false
                              : withMarkup,
                          addCosts,
                          true,
                          dealerTax
                            ? 0
                            : data?.quote.salesTax
                              ? data?.quote.salesTax
                              : 0,
                          freightData &&
                            "crateCharge" in freightData &&
                            "rateQuoteCharge" in freightData &&
                            freightData.crateCharge &&
                            freightData.rateQuoteCharge &&
                            data?.quote.state !== "CA"
                            ? freightData.crateCharge +
                                freightData.rateQuoteCharge
                            : data?.quote.deliveryCharge || 0,
                          "",
                          adminChargeMargined ? data?.quote.adminCharge : "",
                          freightData &&
                            "crateCharge" in freightData &&
                            "rateQuoteCharge" in freightData &&
                            freightData.crateCharge &&
                            freightData.rateQuoteCharge &&
                            data?.quote.state !== "CA"
                            ? userData.crateFreightMarkup
                            : userData.deliveryMarkup,
                          false,
                          Boolean(
                            getDealerTax({
                              status: data?.quote.status || "",
                              dealerTax: userData?.dealerTax || null,
                              dateOrdered: data?.quote.dateOrdered || null,
                            }),
                          ),
                        )
                      }
                      disabled={
                        errMessageMargin || disableChanges || isFetching
                          ? true
                          : false
                      }
                      color="primary"
                      variant="outlined"
                      size="small"
                      style={{ opacity: errMessageMargin ? 0.5 : 1 }}
                      id={
                        priceWithMargin && withMarkup
                          ? "calcBtn"
                          : !priceWithMargin && !withMarkup
                            ? "calcBtn"
                            : ""
                      }
                    >
                      Calculate
                    </StyledCalcButton>
                  </StyledDivAddCosts>

                  {!dealerTax && (
                    <StyledDivRowSpaceBtw>
                      {data?.quote.salesTax !== 0 && data?.quote.salesTax ? (
                        <>
                          <Typography variant="body2">Sales Tax:</Typography>
                          <div style={{ position: "relative" }}>
                            <div style={{ position: "absolute", left: -30 }}>
                              <CustomSalesTax
                                isFetching={isFetching}
                                quoteId={data?.quote._id}
                              />
                            </div>
                            <Typography variant="body2">
                              {data?.quote.salesTax !== 0 &&
                              data?.quote.salesTax
                                ? `${(data?.quote.salesTax * 100).toFixed(3)}%`
                                : ""}
                            </Typography>
                            {!userData.otsm && (
                              <div
                                style={{
                                  position: "absolute",
                                  bottom: -15,
                                  right: 0,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  style={{ fontSize: 10 }}
                                >
                                  (
                                  {calcSalesTaxDollar({
                                    salesTax: data?.quote.salesTax || 0,
                                    retailCost:
                                      data?.quote.suggestedRetailPrice,
                                    addCosts,
                                    marginMarkup: marginInput,
                                    withMarkup,
                                    adminCharge: adminChargeMargined
                                      ? data?.quote.adminCharge
                                      : 0,
                                    customCladPrice:
                                      data?.quote.customCladPrice || 0,
                                    splitFinishPrice:
                                      data?.quote.splitFinishPrice || 0,
                                  })}
                                  )
                                </Typography>
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ position: "absolute", right: 50 }}>
                            <CustomSalesTax
                              isFetching={isFetching}
                              quoteId={data?.quote._id}
                            />
                          </div>

                          <SalesTaxTooltip />
                        </>
                      )}
                    </StyledDivRowSpaceBtw>
                  )}

                  <StyledGrandDiv
                    style={{
                      paddingTop:
                        data?.quote.salesTax !== 0 && data?.quote.salesTax
                          ? 10
                          : 0,
                    }}
                  >
                    <Typography style={{ fontWeight: "bold" }} variant="h6">
                      {userData.otsm &&
                        allDealerActAccess(userData.signInRole) &&
                        "Customer"}{" "}
                      Grand Total:
                    </Typography>
                    <Typography style={{ fontWeight: "bold" }} variant="body1">
                      ${grandTotal}
                    </Typography>
                  </StyledGrandDiv>
                </>
              )}

              <StyledButtonsLayout>
                {(priceWithMargin && withMarkup) ||
                (!priceWithMargin && !withMarkup) ? (
                  <Typography color="primary" style={{ fontWeight: "bold" }}>
                    Press Calcuate to Update Grand Total
                  </Typography>
                ) : load ? (
                  <>
                    <StyledBackdrop2 open={true}>
                      <StyledDivCol>
                        <img
                          src="https://res.cloudinary.com/ag-millworks/image/upload/door_animation_reverse_2.gif"
                          alt="door logo"
                        />
                        <StyledText>Preparing Your Quote...</StyledText>
                      </StyledDivCol>
                    </StyledBackdrop2>

                    <Button
                      variant="contained"
                      disabled
                      style={{
                        backgroundColor: "#21c7f5",
                        color: "black",
                        marginBottom:
                          allDealerActAccess(userData.signInRole) ||
                          userData.signInRole === "Admin"
                            ? 10
                            : 0,
                        marginTop:
                          !allDealerActAccess(userData.signInRole) &&
                          userData.signInRole !== "Admin"
                            ? 10
                            : 0,
                      }}
                    >
                      Generate Full Details
                    </Button>
                  </>
                ) : !pdfErr && !load && (dealerUrl || customerUrl) ? (
                  <>
                    {dealerUrl && (
                      <DealerPDF
                        quoteNumber={data?.quote.quoteNumber}
                        status={data?.quote.status}
                        isOrder={
                          data?.quote.status !== "Quote" &&
                          data?.quote.status !== "Archived"
                        }
                        cantSign={
                          data?.quote.checkedJobsite &&
                          !po &&
                          (!jobsite.address ||
                            !jobsite.city ||
                            !jobsite.zip ||
                            !jobsite.contactName ||
                            !jobsite.contactNumber)
                            ? "jobsite and po"
                            : data?.quote.checkedJobsite &&
                                po &&
                                (!jobsite.address ||
                                  !jobsite.city ||
                                  !jobsite.zip ||
                                  !jobsite.contactName ||
                                  !jobsite.contactNumber)
                              ? "jobsite"
                              : data?.quote.checkedJobsite &&
                                  !po &&
                                  jobsite.address &&
                                  jobsite.city &&
                                  jobsite.zip &&
                                  jobsite.contactName &&
                                  jobsite.contactNumber
                                ? "po"
                                : !data?.quote.checkedJobsite && !po
                                  ? "po"
                                  : data?.quote
                                        .customCladColorPriceUpdateRequired
                                    ? "customCladColorPriceUpdateRequired"
                                    : data?.quote.freightQuote
                                          ?.freightQuotePriceUpdateRequired
                                      ? "freightQuotePriceUpdateRequired"
                                      : ""
                        }
                        dealerUrl={dealerUrl}
                        viewAllState={
                          allDealerActAccess(userData.signInRole) ||
                          userData.signInRole === "Admin"
                            ? true
                            : false
                        }
                        base64Pdf={base64Pdf}
                        canEditCustomCladColorAndFreightQuotePrice={
                          canEditCustomCladColorAndFreightQuotePrice
                        }
                      />
                    )}

                    {customerUrl && (
                      <CustomerPDF
                        signInRole={userData.signInRole}
                        customerEmail={data?.quote.customerEmail || ""}
                        dealerEmail={data?.quote.email || ""}
                        quoteNumber={data?.quote.quoteNumber}
                        openCustomerEmailModal={openCustomerEmailModal}
                        handleClose={handleCloseCustomerEmailModal}
                        openWarning={openWarning}
                        handleCloseWarning={handleCloseWarning}
                        handleOpen={handleOpenCustomerEmailModal}
                        margin={margin}
                        customerUrl={customerUrl}
                        handleOpenWarning={handleOpenWarning}
                        handlePDF={handleGenerateClick}
                        pdfObj={{
                          poNumber: po || "",
                          quoteId: data?.quote._id,
                          grandTotal,
                          isOrder:
                            data?.quote.status !== "Quote" &&
                            data?.quote.status !== "Archived",
                          dealerTax: dealerTax || 0,
                          hideCustomerPricing,
                          salesTax:
                            !dealerTax && data?.quote.salesTax
                              ? data?.quote.salesTax
                              : 0,
                          margin,
                          withMarkup,
                          addCosts,
                          dealerLogo: userData.dealerLogo || "",
                          deliveryMarkup: userData.deliveryMarkup,
                          crateFreightMarkup:
                            userData.crateFreightMarkup || false,
                        }}
                      />
                    )}
                  </>
                ) : !pdfErr && (!dealerUrl || !customerUrl) ? (
                  <Button
                    onClick={
                      fetchingFreight
                        ? handleOpenRecalculatingWarning
                        : handleGenerateClick
                    }
                    disabled={isFetching}
                    variant="contained"
                    style={{
                      backgroundColor: "#21c7f5",
                      color: "black",
                      marginBottom:
                        allDealerActAccess(userData.signInRole) ||
                        userData.signInRole === "Admin"
                          ? 10
                          : 0,
                      marginTop:
                        !allDealerActAccess(userData.signInRole) &&
                        userData.signInRole !== "Admin"
                          ? 10
                          : 0,
                    }}
                  >
                    Generate Full Details
                  </Button>
                ) : !pdfErr ? (
                  <Typography color="primary" style={{ fontWeight: "bold" }}>
                    Press Calcuate to Update Grand Total
                  </Typography>
                ) : (
                  <Alert severity="error">
                    A Problem Occurred Generating the PDFs. Please Contact
                    Technical Assistance at (805) 644-4494
                  </Alert>
                )}
              </StyledButtonsLayout>
              <StyledDivCustomPaint>
                {data?.quote.units.some(custom) && (
                  <Typography style={{ color: "red" }} variant="body2">
                    <span style={{ color: "red", fontWeight: "bold" }}>
                      Custom Paint/Stain Selected:
                    </span>{" "}
                    Price May Change Once Sample is Recieved
                  </Typography>
                )}
              </StyledDivCustomPaint>
            </StyledDivColCenter>
          </StyledPricingDiv>
          {/* // )} */}
        </StyledOSContainer>

        <Copyright position="right" />
      </StyledDivContentWrap>
    </StyledDivBg>
  );
};

export default OrderSummary;
